import {
  Checkbox,
  type CheckboxProps,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  Switch,
  type SwitchProps,
  Tooltip,
} from '@mui/material';
import { Info } from 'lucide-react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';

// Base props for both components
type BaseProps = {
  control: Control<any>;
  name: string;
  label: string;
  helpText?: string;
  tooltip?: string;
  inFormGroup?: boolean;
};

// Helper to omit controlled props
type WithoutControlledProps<T> = Omit<
  T,
  'name' | 'onChange' | 'onBlur' | 'checked' | 'ref'
>;

// Switch variant
type SwitchVariant = BaseProps & {
  useSwitch: true;
} & WithoutControlledProps<SwitchProps>;

// Checkbox variant
type CheckboxVariant = BaseProps & {
  useSwitch?: false;
} & WithoutControlledProps<CheckboxProps>;

type RaCheckboxProps = SwitchVariant | CheckboxVariant;

export const RaCheckbox: React.FC<RaCheckboxProps> = ({
  control,
  name,
  helpText,
  tooltip,
  inFormGroup = false,
  ...props
}) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const controlComponent = (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <FormControlLabel
              control={
                'useSwitch' in props && props.useSwitch ? (
                  <Switch
                    size="small"
                    name={field.name}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    checked={field.value ?? false}
                    {...props}
                  />
                ) : (
                  <Checkbox
                    size="small"
                    name={field.name}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    checked={field.value ?? false}
                    sx={{
                      p: 0.75,
                      borderRadius: '6px',
                      '& .MuiSvgIcon-root': { fontSize: '1.2rem' },
                    }}
                    {...props}
                  />
                )
              }
              label={props.label}
              sx={{
                ...(inFormGroup ? { ml: 0 } : {}),
                my: 0,
              }}
            />
            {tooltip && (
              <Tooltip title={tooltip}>
                <Info size={16} />
              </Tooltip>
            )}
          </Stack>
        );

        if (inFormGroup) {
          return controlComponent;
        }

        return (
          <FormControl component="fieldset" variant="standard" error={!!error}>
            {controlComponent}
            {(error?.message || helpText) && (
              <FormHelperText>{error?.message ?? helpText}</FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
};
